import {
  flexRender,
  getCoreRowModel,
  OnChangeFn,
  PaginationOptions,
  PaginationState,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import BounceLoader from "react-spinners/ClipLoader"; // Import the loader

import { t } from "i18next";
import {
  MdChevronRight,
  MdChevronLeft,
  MdArrowUpward,
  MdArrowDownward,
  MdUnfoldMore,
} from "react-icons/md";
import { useEffect, useRef, useState } from "react";
import ResponsivePagination from "react-responsive-pagination";
import ComboBox from "widgets/shared/inputs/combobox";
import { TableHeightLayout } from "hooks/viewport-dimensions";

type Props<T extends Record<string, string | number>> = {
  data: any;
  columns: any;
  pagination?: PaginationState;
  paginationOptions?: Pick<
    PaginationOptions,
    "onPaginationChange" | "rowCount" | "pageCount"
  >;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  onRowClick?: (row: any) => void;
  loading?: boolean; // Add loading prop
  filterbar?: React.ReactNode;
  noDataLabel?: string;
  compact?: boolean; // Add compact prop
  totalRecords?: number;
  offset?: number;
};

export default function Table<T extends Record<string, string | number>>({
  data,
  columns,
  pagination = { pageIndex: 0, pageSize: 10 },
  paginationOptions = {
    onPaginationChange: () => {},
    rowCount: 0,
    pageCount: 0,
  },
  sorting,
  onSortingChange,
  onRowClick,
  loading, // Destructure the loading prop
  filterbar,
  noDataLabel = "No data to display",
  compact = false, // Destructure compact prop with default false
  totalRecords = 0,
  offset,
}: Props<T>) {
  const [showNoData, setShowNoData] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    if (!loading && data.length === 0) {
      timer = setTimeout(() => setShowNoData(true), 2000); // 2-second delay
    } else {
      setShowNoData(false); // Reset if data is loading or available
    }

    return () => {
      if (timer) clearTimeout(timer); // Clear timeout on cleanup
    };
  }, [loading, data]);

  const table = useReactTable({
    data,
    columns,
    state: { pagination, sorting },
    onSortingChange,
    ...paginationOptions,
    manualFiltering: true,
    manualSorting: true,
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
  });

  const prevPageRef = useRef(table.getState().pagination);

  useEffect(() => {
    const current = table.getState().pagination;
    const prev = prevPageRef.current;

    // Only call onPaginationChange when React Table moved itself
    const changedByUI =
      current.pageIndex !== prev.pageIndex ||
      current.pageSize !== prev.pageSize;

    if (changedByUI) {
      prevPageRef.current = current;
      paginationOptions.onPaginationChange(current);
    }
  }, [
    table.getState().pagination.pageIndex,
    table.getState().pagination.pageSize,
    paginationOptions,
  ]);

  return (
    <TableHeightLayout offset={offset}>
      <div
        className={
          "flex h-full w-full flex-col rounded-[20px] bg-background-900 px-4 md:px-6"
        }
      >
        {filterbar && <div className="!z-10 py-[20px]">{filterbar}</div>}

        <div className="flex h-full flex-col overflow-y-auto  pb-4 xl:overflow-x-auto">
          <table className="w-full">
            <thead className="sticky top-0 !z-0 bg-background-50">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="">
                  {headerGroup.headers.map((header) => {
                    const customHeadClass = (
                      header.column.columnDef.meta as any
                    )?.customHeadClass;
                    return (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                        onClick={header.column.getToggleSortingHandler()}
                        className={`heading-xs text-text-900 h-10 cursor-pointer bg-background-50 p-3 ${
                          compact ? "px-2 py-1 text-xs" : ""
                        } ${customHeadClass}`}
                        style={{
                          ...(header.column.columnDef.meta as any)?.style,
                        }}
                      >
                        <div
                          className={`flex items-center gap-6 text-nowrap text-white ${
                            compact ? "gap-2" : ""
                          }`}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {header.column.getIsSorted() === "asc" ? (
                            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-white text-secondary">
                              <MdArrowUpward size={16} />
                            </span>
                          ) : header.column.getIsSorted() === "desc" ? (
                            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-white text-secondary">
                              <MdArrowDownward size={16} />
                            </span>
                          ) : header.column.getCanSort() ? (
                            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-white text-secondary">
                              <MdUnfoldMore size={16} />
                            </span>
                          ) : (
                            <span></span>
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>

            {/* Scrollable Table Body */}

            <tbody className="">
              {loading ? (
                <tr>
                  <td colSpan={columns.length} className="p-4 text-center">
                    <BounceLoader color="#333333" />
                  </td>
                </tr>
              ) : showNoData ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="p-4 text-center text-txt-md text-secondary"
                  >
                    {noDataLabel}
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className={`border-b border-gray-200 ${
                      compact ? "text-xs" : ""
                    }`}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const isClickable = (cell.column.columnDef.meta as any)
                        ?.rowClick;
                      const customClass = (cell.column.columnDef.meta as any)
                        ?.customClass;

                      return (
                        <td
                          key={cell.id}
                          className={`min-w-[50px] max-w-[500px] text-nowrap text-secondary ${
                            compact ? "h-6 p-1 text-xs" : "h-10 p-2 text-txt-sm"
                          } ${
                            isClickable ? "cursor-pointer" : ""
                          } ${customClass}`}
                          onClick={(e) => {
                            if (isClickable) {
                              onRowClick && onRowClick(row.original);
                            } else {
                              e.stopPropagation();
                            }
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="grow"></div>

        <div
          className={`mb-2 mt-2 flex h-auto flex-col justify-between md:h-20 md:flex-row md:items-center md:gap-4 ${
            compact ? "h-10 py-2" : ""
          }`}
        >
          {/* left side */}
          {paginationOptions.pageCount > 1 ? (
            <div
              className={`flex items-center justify-between gap-4 ${
                compact ? "gap-2" : ""
              }`}
            >
              <div>
                <p
                  className={`text-sm text-secondary ${
                    compact ? "text-xs" : ""
                  }`}
                >
                  {paginationOptions.rowCount
                    ? t("table.status.rowsPerPage", {
                        offset:
                          table.getState().pagination.pageIndex *
                            table.getState().pagination.pageSize +
                          1,
                        end: Math.min(
                          (table.getState().pagination.pageIndex + 1) *
                            table.getState().pagination.pageSize,
                          paginationOptions.rowCount
                        ),

                        total: paginationOptions.rowCount,
                      })
                    : t("table.status.noData")}
                </p>
              </div>
              <div>
                <ComboBox
                  id={"filter"}
                  extra="w-20"
                  value={table.getState().pagination.pageSize}
                  onChange={(val) => {
                    table.setPagination({
                      pageIndex: 0,
                      pageSize: val,
                    });
                  }}
                  items={[
                    { label: "10", value: 10 },
                    { label: "20", value: 20 },
                    { label: "30", value: 30 },
                    { label: "40", value: 40 },
                    { label: "50", value: 50 },
                    { label: "100", value: 100 },
                    { label: "200", value: 200 },
                  ]}
                  clearIcon={false}
                  sizes={compact ? "xs" : "sm"}
                  rounded="sm"
                  placeholder={""}
                  color="secondary"
                />
              </div>
            </div>
          ) : (
            <p className={`text-sm text-secondary ${compact ? "text-xs" : ""}`}>
              Total records: {totalRecords || paginationOptions.rowCount}
            </p>
          )}

          {/* Pagination */}
          {paginationOptions.pageCount > 1 && (
            <div className="mt-2 flex w-full items-center justify-center md:mt-0 md:w-auto md:justify-end">
              <div
                className="flex items-center gap-2"
                style={{
                  visibility:
                    paginationOptions.pageCount > 1 ? "visible" : "hidden",
                }}
              >
                <ResponsivePagination
                  current={table.getState().pagination.pageIndex + 1}
                  total={table.getPageCount()}
                  onPageChange={(pageNumber) => {
                    table.setPageIndex(pageNumber - 1);
                  }}
                  className={`flex h-12 items-center text-sm font-bold text-secondary ${
                    compact ? "h-8 text-xs" : ""
                  }`}
                  pageItemClassName={`p-4 mx-1 h-4 w-4 items-center justify-center flex text-txt-sm border border-background-50 ${
                    compact ? "p-2 mx-0 h-3 w-3 text-xs" : ""
                  }`}
                  activeItemClassName={`bg-background-50 text-white ${
                    compact ? "text-xs" : ""
                  }`}
                  navClassName="border-0"
                  previousLabel={
                    <>
                      <MdChevronLeft className="h-6 w-6 text-secondary" />
                    </>
                  }
                  nextLabel={
                    <>
                      <MdChevronRight className="h-6 w-6 text-secondary" />
                    </>
                  }
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </TableHeightLayout>
  );
}
